import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup
} from '@angular/forms';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import {
  CustomerCreditStatus,
  CustomerType,
  LimitRulesType,
  LoanClass,
  ModalDialogType,
  Personality,
} from 'src/app/@core/constants';
import { PosPsychometricEvaluationModalService } from 'src/app/@core/services/pos-psychometric-evaluation-modal.service';
import { ColumnTable } from 'src/app/models/column-table';
import { Customer } from 'src/app/models/customer';
import { Paging } from 'src/app/models/page';
import { PreApproveCustomerParams } from 'src/app/models/params/preApprove-customer-params';
import { CreditStatusFranchisedResponseModel } from 'src/app/models/response/customerCreditStatusResponseModel';
import { AuthService } from 'src/app/services/auth.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { OnbaseService } from 'src/app/services/onbase.service';
import { CustomerValidationsService } from '../../services/customer-validations.service';
import { CustomerPreApprovalModalIncomeFormComponent } from '../customer-pre-approval-modal-income-form/customer-pre-approval-modal-income-form.component';
import { CreditPlanRequirementsListModalService } from './../../../../@core/services/credit-plan-requirements-list-modal.service';
import { CreditReferencesResponseModel } from './../../../../models/response/creditReferencesResponseModel';
import { OnbaseProcedureResponseModel } from './../../../../models/response/onbaseProcedureResponseModel';
import { PreApproveCustomerResponseModel } from './../../../../models/response/preApproveCustomerResponseModel';
import { CustomerTableModel } from './../../../../models/table-models/customer-table';
import { ProductTableModel } from './../../../../models/table-models/product-table';
import { User } from './../../../../models/user';
import { CreditService } from './../../../../services/credit.service';

@Component({
  selector: 'app-customer-credit-plans',
  templateUrl: './customer-credit-plans.component.html',
  styleUrls: ['./customer-credit-plans.component.sass'],
})
export class CustomerCreditPlansComponent implements OnInit {
  @Input() currentCustomer: Customer = new Customer();
  @Input() currentCustomerCreditStatus: CreditStatusFranchisedResponseModel =
    new CreditStatusFranchisedResponseModel();

  @Input() creditPlans: Paging<Array<OnbaseProcedureResponseModel>> =
    new Paging([]);

  @Input() selectedProduct: ProductTableModel | undefined;
  @Input() selectedSeller: User | CustomerTableModel | undefined;
  @Input() preApprovalForm: FormGroup | undefined;

  @Output() preApproveCreditPlan: EventEmitter<{
    preApproveParams: PreApproveCustomerParams;
    creditPlan: OnbaseProcedureResponseModel;
    monthIncome: number | undefined;
  }> = new EventEmitter();

  @Output() quotationPlanClick: EventEmitter<boolean> = new EventEmitter(false);

  seletectCreditPlan!: OnbaseProcedureResponseModel;
  creditReferences: CreditReferencesResponseModel =
    new CreditReferencesResponseModel();
  comesFromQuotationButton = false;

  constructor(
    private onbaseService: OnbaseService,
    private configurationService: ConfigurationService,
    private creditService: CreditService,
    private authService: AuthService,
    private creditPlanRequirementsListModalService: CreditPlanRequirementsListModalService,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private router: Router,
    private customerValidationsService: CustomerValidationsService,
    private modalService: BsModalService,
    private psychoEvalModalService: PosPsychometricEvaluationModalService
  ) { }

  async ngOnInit(): Promise<void> {
    // await this.getCreditPlansData();

    this.creditReferences = (
      await this.creditService
        .getCreditReferences(
          this.configurationService.readSelectedBranchAndSalesOrg().salesOrg
            .code,
          undefined,
          this.getLoanClass()
        )
        .toPromise()
    ).response[0];
  }

  getCreditPlansHeaders(): Array<ColumnTable> {
    return [
      new ColumnTable('name', 'Plan'),
      new ColumnTable('credit', 'Crédito', { type: 'currency' }),
      new ColumnTable('cash', 'Efectivo', { type: 'currency' }),
      new ColumnTable('writtenDuration', 'NAS'),
      new ColumnTable('code', 'Opciones', {
        type: 'action',
        actions: [
          {
            fn: (creditPlan: OnbaseProcedureResponseModel) =>
              this.handleViewRequirements(creditPlan),
            icon: 'fa fa-list-ol',
            tooltip: 'Ver requisitos',
            isDisabled: false,
          },
          {
            fn: (creditPlan: OnbaseProcedureResponseModel) =>
              this.handlePreApproveCreditPlan(creditPlan),
            icon: 'fa fa-regular fa-circle-check',
            tooltip: 'Pre aprobar plan',
            isDisabled: false,
            disabledFn: (creditPlan: OnbaseProcedureResponseModel) =>
              creditPlan.preApprove || creditPlan.evaluate,
          },
          {
            fn: async (creditPlan: OnbaseProcedureResponseModel) => {
              await this.psychometricEvaluation(creditPlan)
            },
            icon: 'fa fa-external-link',
            tooltip: 'Evaluación Solvenza',
            isDisabled: false,
            disabledFn: (creditPlan: OnbaseProcedureResponseModel) =>
              !creditPlan.evaluate,
          },
          {
            fn: (creditPlan: OnbaseProcedureResponseModel) =>
              this.handleCreditRequestClick(creditPlan),
            icon: 'fa fa-pen',
            tooltip: 'Ingresar Solicitud',
            isDisabled: true,
            disabledFn: (creditPlan: OnbaseProcedureResponseModel) =>
              !creditPlan.preApprove,
          },
          {
            fn: (creditPlan: OnbaseProcedureResponseModel) =>
              this.handleCreateQuotationClick(creditPlan),
            icon: 'fa fa-file',
            tooltip: 'Cotizar',
            isDisabled: true,
            disabledFn: (creditPlan: OnbaseProcedureResponseModel) =>
              !creditPlan.preApprove,
          },
        ],
      }),
    ];
  }
  async psychometricEvaluation(creditPlan: OnbaseProcedureResponseModel) {
    await this.psychoEvalModalService.show(this.currentCustomer, async () => {
      creditPlan.evaluate = false;
      await this.handlePreApproveCreditPlan(creditPlan);
    });
  }

  async getCreditPlansData(): Promise<void> {
    const sellerCode = this.isCustomer(this.selectedSeller)
      ? this.selectedSeller.code
      : this.selectedSeller!.username;

    this.creditPlans = (
      await this.onbaseService
        .readPreApproveCreditPlans(
          this.currentCustomer.code,
          this.configurationService.readSelectedBranchAndSalesOrg().branch.code,
          LimitRulesType.BURO,
          this.configurationService.readSelectedBranchAndSalesOrg().salesOrg
            .code,
          "",
          sellerCode
        )
        .toPromise()
    ).response;
  }

  public async handlePreApproveCreditPlan(
    creditPlan: OnbaseProcedureResponseModel
  ): Promise<void> {
    this.creditPlans.content
      .filter((plan) => !this.isImmediateCreditPlan(plan))
      .forEach((plan) => {
        plan.preApprove = false;
        plan.credit = 0;
        plan.cash = 0;
      });
    this.preApprovalForm?.patchValue({ monthIncome: undefined });
    this.preApprovalForm?.clearValidators();
    let incomeInfo = undefined;

    if (creditPlan) {
      this.seletectCreditPlan = creditPlan;

      const canContinue =
        await this.customerValidationsService.validateOnBaseCreditStatus(
          this.currentCustomer.code
        );

      if (canContinue) {
        if (this.isNormalCreditPlan(creditPlan) || this.isNBAPlan(creditPlan)) {
          const modalRef: BsModalRef<CustomerPreApprovalModalIncomeFormComponent> =
            this.modalService.show(
              CustomerPreApprovalModalIncomeFormComponent,
              {
                backdrop: true,
                ignoreBackdropClick: true,
                keyboard: false,
                animated: true,
                initialState: {
                  preApprovalForm: this.preApprovalForm,
                },
                class: `modal-dialog ${ModalDialogType.PRIMARY}`,
              }
            );

          incomeInfo = await modalRef.content!.incomeInfoChange.toPromise();

          if (incomeInfo !== undefined) {
            this.emitPreApproveParams(creditPlan, incomeInfo);
          }
        } else {
          this.emitPreApproveParams(creditPlan, incomeInfo);
        }
      }
    }
  }

  private emitPreApproveParams(
    creditPlan: OnbaseProcedureResponseModel,
    incomeInfo: number | undefined
  ) {
    const preApproveCustomerParams: PreApproveCustomerParams =
      this.buildPreApproveParams(creditPlan);

    this.preApproveCreditPlan.emit({
      preApproveParams: preApproveCustomerParams,
      creditPlan,
      monthIncome: incomeInfo,
    });
  }

  private isNormalCreditPlan(creditPlan: OnbaseProcedureResponseModel) {
    return creditPlan.code === `0005`;
  }

  private isImmediateCreditPlan(creditPlan: OnbaseProcedureResponseModel) {
    return creditPlan.code === `0002`;
  }

  private isNBAPlan(creditPlan: OnbaseProcedureResponseModel) {
    return creditPlan.code === `0003`;
  }

  private async preApprovePlan(
    preApproveCustomerParams: PreApproveCustomerParams
  ): Promise<PreApproveCustomerResponseModel> {
    return await this.creditService
      .getPreApproveCreditInfo(preApproveCustomerParams)
      .toPromise();
  }

  private buildPreApproveParams(
    creditPlan: OnbaseProcedureResponseModel
  ): PreApproveCustomerParams {
    return {
      customerCode: this.currentCustomer.code,
      fiscalId: this.currentCustomer.mainPersonalInfo?.fiscalId
        ? this.currentCustomer.mainPersonalInfo?.fiscalId
        : '',
      fiscalIdType: this.currentCustomer.mainPersonalInfo?.fiscalIdType
        ? this.currentCustomer.mainPersonalInfo?.fiscalIdType
        : '',
      branchCode:
        this.configurationService.readSelectedBranchAndSalesOrg().branch.code,
      limitRule: creditPlan.limitRule,
      salesOrg:
        this.configurationService.readSelectedBranchAndSalesOrg().salesOrg.code,
      sellerCode: this.isCustomer(this.selectedSeller)
        ? this.selectedSeller.code
        : this.selectedSeller!.username,
      menu: 'CUSTOMERS',
      onbaseProcedureCode: creditPlan.code,
      productCode: this.selectedProduct!.code,
    };
  }

  private async handleViewRequirements(
    creditPlan: OnbaseProcedureResponseModel
  ): Promise<void> {
    this.creditPlanRequirementsListModalService.currentCredidPlan = creditPlan;
    this.creditPlanRequirementsListModalService.show();
  }

  private async handleCreditRequestClick(
    creditPlan: OnbaseProcedureResponseModel
  ): Promise<void> {
    this.router.navigate(['creditRequests/create'], {
      state: {
        creditPlan,
        customer: this.currentCustomer,
        customerCreditConditions: this.currentCustomerCreditStatus,
        seller: this.selectedSeller
      },
      replaceUrl: true,
    });

    this.creditService.setPreApprove({
      creditPlan,
      isCreditRequestCreated: true,
      preApproveCode: '',
      preApproveAmount: 0
    });
  }

  private getLoanClass(): string {
    let loanClass = ``;

    if (
      this.currentCustomerCreditStatus.estadoCliente ===
      CustomerCreditStatus.COVENANT
    ) {
      return LoanClass.CLASS_FOR_COVENANT;
    }

    if (this.currentCustomer.customerType === CustomerType.EMPLOYEE) {
      return LoanClass.CLASS_FOR_EMPLOYEE;
    }

    if (
      this.currentCustomer.mainPersonalInfo?.personality === Personality.PERSON
    ) {
      return LoanClass.CLASS_FOR_PERSON;
    } else if (
      this.currentCustomer.mainPersonalInfo?.personality ===
      Personality.LEGAL_ENTITY
    ) {
      // TODO: ver porque para juridico no se pone clase de contrato??
      loanClass = ``;
    }
    return loanClass;
  }

  isCustomer(
    value: CustomerTableModel | User | undefined
  ): value is CustomerTableModel {
    return (value as CustomerTableModel).fullName !== undefined;
  }

  private handleCreateQuotationClick(creditPlan: OnbaseProcedureResponseModel) {
    let employee = new CustomerTableModel();
    employee.code = (this.selectedSeller as User).username;
    employee.fullName = (this.selectedSeller as User).fullname;
    creditPlan.credit = this.creditService.getPreApprove()?.preApproveAmount || 0;
    this.quotationPlanClick.emit(true);
    this.router.navigate(['sales'], {
      state: {
        creditPlan,
        customer: this.currentCustomer,
        preApproveCode: this.creditService.getPreApprove()?.preApproveCode,
        employee: employee
      },
      replaceUrl: true,
    });
  }
}
